import React, { useState, useMemo, useEffect } from 'react';
import { BookOpen, Star, CaretRight, FileText, FolderSimple, Trash, Plus, ToggleLeft, ToggleRight, SpinnerGap, Check, X, Package, Gear } from '@phosphor-icons/react';

const FILE_ICONS: Record<string, string> = {
  pdf: 'var(--ds-danger)', docx: 'var(--ds-accent)', md: 'var(--ds-success)',
  json: 'var(--ds-warning)', txt: 'var(--ds-text-muted)', default: 'var(--ds-text-muted)',
};

interface ContextItem {
  key: string;
  content: string;
  content_type?: string;
  enabled?: number;
}

interface FileEntry { name: string; type: string; content: string; contextKey: string; }

const SKILL_COLORS = ['var(--ds-accent)', 'var(--ds-success)', 'var(--ds-warning)', 'var(--ds-accent)', 'var(--ds-danger)', 'var(--ds-accent)'];

interface DebugPanelProps {
  open: boolean;
  onClose: () => void;
  contextItems?: ContextItem[];
  mode?: 'view' | 'manage';
  activeFileName?: string | null;
  disabledKeys?: Set<string>;
  pendingToggleKeys?: Set<string>;
  onToggleFile?: (key: string, enabled: boolean) => void;
  onViewFile?: (name: string, content: string) => void;
  onDeleteFile?: (name: string) => void;
  onAddFile?: (section: 'memory' | 'context' | 'skill', skillGroup?: string) => void;
  onManage?: (type: 'skill' | 'memory') => void;
  onEditSave?: () => void;
  onEditDiscard?: () => void;
  artifacts?: { id: string; filename: string; key: string; created_at?: string }[];
  onViewArtifact?: (key: string) => void;
  onUpload?: () => void;
}

export default function DebugPanel({
  open, onClose,
  contextItems = [], mode = 'view', activeFileName,
  disabledKeys = new Set(), pendingToggleKeys = new Set(), onToggleFile,
  onViewFile, onDeleteFile, onAddFile, onManage,
  onEditSave, onEditDiscard,
  artifacts = [], onViewArtifact, onUpload,
}: DebugPanelProps) {
  const [memoryExpanded, setMemoryExpanded] = useState(true);
  const [contextExpanded, setContextExpanded] = useState(true);
  const [artifactsExpanded, setArtifactsExpanded] = useState(true);
  const [skillsExpanded, setSkillsExpanded] = useState(false);
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{ name: string; input: string } | null>(null);

  const isManage = mode === 'manage';

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  // Parse contextItems into sections
  const { memoryFiles, contextFiles, skillGroups } = useMemo(() => {
    const mem: FileEntry[] = [];
    const ctx: FileEntry[] = [];
    const skillMap = new Map<string, FileEntry[]>();

    contextItems.forEach(c => {
      if (!c.content && !(c as any)._binary) return;
      if (c.key.startsWith('memory/')) {
        const name = c.key.slice(7);
        mem.push({ name, type: name.split('.').pop() || 'md', content: c.content, contextKey: c.key });
      } else if (c.key.startsWith('skill/')) {
        const rest = c.key.slice(6);
        const slashIdx = rest.indexOf('/');
        if (slashIdx > 0) {
          const group = rest.slice(0, slashIdx);
          const name = rest.slice(slashIdx + 1);
          if (!skillMap.has(group)) skillMap.set(group, []);
          skillMap.get(group)!.push({ name, type: name.split('.').pop() || 'md', content: c.content, contextKey: c.key });
        }
      } else {
        const name = c.key.startsWith('context/') ? c.key.slice(8) : c.key;
        ctx.push({ name, type: name.split('.').pop() || 'md', content: c.content, contextKey: c.key });
      }
    });

    const groups = [...skillMap.entries()].map(([name, files], i) => ({
      name, files, color: SKILL_COLORS[i % SKILL_COLORS.length],
    }));

    return { memoryFiles: mem, contextFiles: ctx, skillGroups: groups };
  }, [contextItems]);

  const renderFileItem = (f: FileEntry, indent = false) => {
    const isActive = isManage && activeFileName === f.name;
    const isDisabled = disabledKeys.has(f.contextKey);
    const isPending = pendingToggleKeys.has(f.contextKey);
    const iconColor = FILE_ICONS[f.type] || FILE_ICONS.default;
    return (
      <div
        key={f.contextKey}
        onClick={() => onViewFile?.(f.name, f.content)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 16px 5px 36px', cursor: 'pointer',
          fontSize: 'var(--fs-sm)', transition: 'background 0.1s',
          color: isActive ? 'var(--brand)' : 'var(--text-secondary)',
          opacity: isDisabled ? 0.45 : 1,
          background: isActive ? 'var(--bg-hover, var(--ds-shadow-color))' : 'transparent',
        }}
        onMouseOver={e => { if (!isActive) e.currentTarget.style.background = 'var(--ds-shadow-color)'; }}
        onMouseOut={e => { e.currentTarget.style.background = isActive ? 'var(--bg-hover, var(--ds-shadow-color))' : 'transparent'; }}
      >
        <FileText size={13} weight="light" style={{ color: isDisabled ? 'var(--text-muted)' : iconColor, flexShrink: 0 }} />
        <span style={{
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          textDecoration: isDisabled ? 'line-through' : 'none',
        }}>{f.name}</span>
        {isManage && (
          <button
            onClick={e => { e.stopPropagation(); if (!isPending) onToggleFile?.(f.contextKey, isDisabled); }}
            disabled={isPending}
            style={{
              background: 'none', border: 'none',
              cursor: isPending ? 'not-allowed' : 'pointer', padding: 0,
              display: 'flex', alignItems: 'center',
              color: isPending ? 'var(--brand)' : isDisabled ? 'var(--text-muted)' : 'var(--ds-success)',
              flexShrink: 0, opacity: isPending ? 0.7 : 1,
            }}
          >
            {isPending
              ? <SpinnerGap size={14} weight="light" className="animate-spin" />
              : isDisabled
                ? <ToggleLeft size={14} weight="light" />
                : <ToggleRight size={14} weight="light" />
            }
          </button>
        )}
        {isManage && (
          <button
            onClick={e => { e.stopPropagation(); setDeleteConfirm({ name: f.name, input: '' }); }}
            className="debug-file-delete"
            style={{
              opacity: 0, width: 16, height: 16, borderRadius: 4, border: 'none',
              background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: 'var(--text-muted)', transition: 'all 0.12s',
            }}
          >
            <Trash size={11} weight="light" />
          </button>
        )}
      </div>
    );
  };

  const renderAddBtn = (section: 'memory' | 'context' | 'skill', skillGroup?: string) => {
    if (!isManage) return null;
    return (
      <button
        onClick={() => onAddFile?.(section, skillGroup)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', margin: '2px 36px',
          borderRadius: 5, fontSize: 'var(--fs-xs)', cursor: 'pointer', fontFamily: 'inherit',
          border: '1px dashed var(--border)', background: 'none',
          color: 'var(--text-muted)', transition: 'all 0.12s',
        }}
        onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)'; }}
        onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
      >
        <Plus size={10} weight="light" /> Add file
      </button>
    );
  };

  const renderSection = (
    label: string, icon: React.ReactNode, count: number,
    expanded: boolean, toggle: () => void,
    children: React.ReactNode,
  ) => (
    <>
      <div
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', cursor: 'pointer', userSelect: 'none',
          transition: 'background 0.1s',
        }}
        onMouseOver={e => { e.currentTarget.style.background = 'var(--ds-shadow-color)'; }}
        onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{
          fontSize: 10, color: 'var(--text-muted)', width: 14, textAlign: 'center',
          display: 'inline-flex', transition: 'transform 0.15s',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
        }}>▶</span>
        {icon}
        <span style={{
          fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1,
        }}>{label}</span>
        {count > 0 && (
          <span style={{
            fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
            background: 'var(--ds-shadow-color)', padding: '1px 6px', borderRadius: 8,
          }}>{count}</span>
        )}
      </div>
      {expanded && <div style={{ padding: '2px 0' }}>{children}</div>}
    </>
  );

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500 }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'var(--ds-overlay)', backdropFilter: 'blur(2px)' }}
      />

      {/* Panel */}
      <div style={{
        position: 'absolute', top: 52, right: 12,
        width: 340, maxHeight: 'calc(var(--app-height) - 68px)',
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 12, boxShadow: '0 20px 60px var(--ds-shadow-color)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        animation: 'debugPanelIn 0.18s ease',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Gear size={15} weight="light" style={{ color: 'var(--text-muted)' }} />
            <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)' }}>Deck Context</span>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 24, height: 24, borderRadius: 6, border: 'none', background: 'none',
              color: 'var(--text-muted)', cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 14, transition: 'all 0.12s',
            }}
            onMouseOver={e => { e.currentTarget.style.background = 'var(--ds-shadow-color)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text-muted)'; }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {renderSection('Memory', <BookOpen size={13} weight="light" style={{ color: 'var(--text-muted)' }} />,
            memoryFiles.length, memoryExpanded, () => setMemoryExpanded(p => !p), <>
              {memoryFiles.map(f => renderFileItem(f))}
              {renderAddBtn('memory')}
            </>
          )}

          {renderSection('Context', <FileText size={13} weight="light" style={{ color: 'var(--brand)' }} />,
            contextFiles.length, contextExpanded, () => setContextExpanded(p => !p), <>
              {contextFiles.map(f => renderFileItem(f))}
              {contextFiles.length === 0 && !isManage && (
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', padding: '4px 36px', fontStyle: 'italic' }}>
                  No files uploaded
                </div>
              )}
              {renderAddBtn('context')}
            </>
          )}

          {renderSection('Artifacts', <Package size={13} weight="light" style={{ color: 'var(--ds-accent)' }} />,
            artifacts.length, artifactsExpanded, () => setArtifactsExpanded(p => !p), <>
              {artifacts.length === 0 && (
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', padding: '4px 36px', fontStyle: 'italic' }}>
                  No artifacts yet
                </div>
              )}
              {artifacts.map(a => (
                <div
                  key={a.id}
                  onClick={() => onViewArtifact?.(a.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '5px 16px 5px 36px', cursor: 'pointer',
                    fontSize: 'var(--fs-sm)', transition: 'background 0.1s',
                    color: 'var(--text-secondary)',
                  }}
                  onMouseOver={e => { e.currentTarget.style.background = 'var(--ds-shadow-color)'; }}
                  onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <FileText size={12} weight="light" style={{ color: 'var(--ds-accent)', flexShrink: 0 }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.filename}</span>
                </div>
              ))}
            </>
          )}

          {skillGroups.length > 0 && renderSection('Skills',
            <Star size={13} weight="light" style={{ color: 'var(--ds-accent)' }} />,
            skillGroups.reduce((n, g) => n + g.files.length, 0),
            skillsExpanded, () => setSkillsExpanded(p => !p), <>
              {skillGroups.map(skill => (
                <div key={skill.name}>
                  <div
                    onClick={() => setExpandedSkills(prev => ({ ...prev, [skill.name]: !prev[skill.name] }))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 16px 5px 36px', cursor: 'pointer',
                      fontSize: 'var(--fs-sm)', transition: 'background 0.1s',
                      color: 'var(--text-secondary)',
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = 'var(--ds-shadow-color)'; }}
                    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{
                      fontSize: 9, color: 'var(--text-muted)',
                      display: 'inline-flex', transition: 'transform 0.15s',
                      transform: expandedSkills[skill.name] ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}>▶</span>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: skill.color, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{skill.name}</span>
                    <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>{skill.files.length}</span>
                  </div>
                  {expandedSkills[skill.name] && (
                    <div style={{ paddingLeft: 16 }}>
                      {skill.files.map(f => renderFileItem(f, true))}
                      {renderAddBtn('skill', skill.name)}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer: Manage / Save+Discard */}
        <div style={{
          padding: '8px 12px', borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        }}>
          {isManage ? (
            <>
              <button
                onClick={onEditDiscard}
                style={{
                  flex: 1, padding: '6px 0', fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
                  background: 'var(--bg-surface)', color: 'var(--text-secondary)',
                  border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                <X size={12} weight="light" /> Discard
              </button>
              <button
                onClick={onEditSave}
                style={{
                  flex: 1, padding: '6px 0', fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
                  background: 'var(--brand)', color: 'var(--ds-on-accent)', fontWeight: 500,
                  border: 'none', borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                <Check size={12} weight="light" /> Save
              </button>
            </>
          ) : (
            <button
              onClick={() => onManage?.('skill')}
              style={{
                width: '100%', padding: '6px 0', fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
                background: 'none', color: 'var(--text-muted)',
                border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                transition: 'all 0.12s',
              }}
              onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)'; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              <Gear size={12} weight="light" /> Manage
            </button>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div
          onClick={() => setDeleteConfirm(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'var(--ds-overlay)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            width: 340, padding: 24, borderRadius: 12,
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            boxShadow: '0 8px 30px var(--ds-shadow-color)',
          }}>
            <div style={{ fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--text-primary)', marginBottom: 8 }}>
              Delete file
            </div>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
              Type <strong style={{ color: 'var(--text-primary)', fontFamily: 'monospace', background: 'var(--code-bg)', padding: '1px 5px', borderRadius: 3, fontSize: 'var(--fs-xs)' }}>{deleteConfirm.name}</strong> to confirm.
            </div>
            <input
              autoFocus
              value={deleteConfirm.input}
              onChange={e => setDeleteConfirm({ ...deleteConfirm, input: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter' && deleteConfirm.input === deleteConfirm.name) {
                  onDeleteFile?.(deleteConfirm.name);
                  setDeleteConfirm(null);
                } else if (e.key === 'Escape') setDeleteConfirm(null);
              }}
              placeholder="Type filename to confirm"
              style={{
                width: '100%', padding: '8px 12px', fontSize: 'var(--fs-sm)',
                borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg-primary)', color: 'var(--text-primary)',
                fontFamily: 'monospace', outline: 'none', marginBottom: 16, boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{
                padding: '6px 14px', fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
                background: 'none', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer',
              }}>Cancel</button>
              <button
                disabled={deleteConfirm.input !== deleteConfirm.name}
                onClick={() => { onDeleteFile?.(deleteConfirm.name); setDeleteConfirm(null); }}
                style={{
                  padding: '6px 14px', fontSize: 'var(--fs-sm)', fontFamily: 'inherit', fontWeight: 500,
                  background: deleteConfirm.input === deleteConfirm.name ? 'var(--ds-danger)' : 'var(--bg-surface)',
                  color: deleteConfirm.input === deleteConfirm.name ? 'var(--ds-on-accent)' : 'var(--text-muted)',
                  border: 'none', borderRadius: 8,
                  cursor: deleteConfirm.input === deleteConfirm.name ? 'pointer' : 'not-allowed',
                }}
              >Delete</button>
            </div>
          </div>
        </div>
      )}

      {isManage && <style>{`div:hover > .debug-file-delete { opacity: 1 !important; }`}</style>}
      <style>{`
        @keyframes debugPanelIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
